import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, FilePen, Save, FileDown, Eye } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SignatureCanvas } from "@/components/SignatureCanvas";
import { exportContractAmendmentToPDF } from "@/lib/contractPdfExport";
import { saveDocumentToEmployeeFile, getDocumentCategory, getDocumentTypeName } from "@/lib/documentStorage";
import { PdfPreviewDialog } from "./PdfPreviewDialog";

interface Employee {
  id: string;
  name: string;
  position: string;
  address_street: string | null;
  address_zip: string | null;
  address_city: string | null;
  department: string | null;
}

interface SalaryHistory {
  base_salary: number;
  salary_type: string;
}

interface CompanySettings {
  name: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
}

export const ContractAmendmentForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [showSignature, setShowSignature] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  
  const [formData, setFormData] = useState({
    selectedEmployeeId: "",
    personName: "",
    personAddress: "",
    oldPosition: "",
    newPosition: "",
    oldSalary: "",
    newSalary: "",
    salaryType: "monthly",
    documentDate: new Date().toISOString().split("T")[0],
    effectiveDate: "",
    amendmentReason: "",
    additionalChanges: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, companyRes] = await Promise.all([
        supabase.from("employees").select("id, name, position, address_street, address_zip, address_city, department").is("archived_at", null),
        supabase.from("company_settings").select("name, street, house_number, postal_code, city").single()
      ]);

      if (employeesRes.data) setEmployees(employeesRes.data);
      if (companyRes.data) setCompanySettings(companyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = async (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (!employee) return;

    // Fetch current salary
    const { data: salaryData } = await supabase
      .from("salary_history")
      .select("base_salary, salary_type")
      .eq("employee_id", employeeId)
      .order("effective_date", { ascending: false })
      .limit(1)
      .single();

    setFormData(prev => ({
      ...prev,
      selectedEmployeeId: employeeId,
      personName: employee.name,
      personAddress: `${employee.address_street || ""}, ${employee.address_zip || ""} ${employee.address_city || ""}`,
      oldPosition: employee.position,
      newPosition: employee.position,
      oldSalary: salaryData?.base_salary?.toString() || "",
      newSalary: salaryData?.base_salary?.toString() || "",
      salaryType: salaryData?.salary_type || "monthly",
    }));
  };

  const handleSave = async (signatureUrl?: string) => {
    if (!user) return;
    if (!formData.selectedEmployeeId || !formData.effectiveDate) {
      toast.error("Bitte wählen Sie einen Mitarbeiter und ein Gültigkeitsdatum");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("contract_templates").insert({
        template_type: "contract_amendment",
        employee_id: formData.selectedEmployeeId,
        person_name: formData.personName,
        person_address_street: formData.personAddress,
        old_position: formData.oldPosition,
        new_position: formData.newPosition,
        old_salary: parseFloat(formData.oldSalary) || null,
        new_salary: parseFloat(formData.newSalary) || null,
        salary_type: formData.salaryType,
        effective_date: formData.effectiveDate,
        amendment_reason: formData.amendmentReason,
        custom_text: formData.additionalChanges,
        signature_url: signatureUrl,
        signed_at: signatureUrl ? new Date().toISOString() : null,
        signed_by: signatureUrl ? formData.selectedEmployeeId : null,
        status: signatureUrl ? "signed" : "draft",
        created_by: user.id,
      });

      if (error) throw error;

      // Update employee record if position changed
      if (formData.oldPosition !== formData.newPosition) {
        await supabase.from("employees").update({
          position: formData.newPosition,
        }).eq("id", formData.selectedEmployeeId);
      }

      // Add salary history entry if salary changed
      if (formData.oldSalary !== formData.newSalary && formData.newSalary) {
        await supabase.from("salary_history").insert({
          employee_id: formData.selectedEmployeeId,
          effective_date: formData.effectiveDate,
          base_salary: parseFloat(formData.newSalary),
          salary_type: formData.salaryType,
          currency: "EUR",
          reason: formData.amendmentReason || "Vertragsänderung",
          created_by: user.id,
        });
      }

      // Create + archive PDF to employee documents
      try {
        const pdfDoc = await exportContractAmendmentToPDF(
          {
            personName: formData.personName,
            personAddress: formData.personAddress,
            oldPosition: formData.oldPosition,
            newPosition: formData.newPosition,
            oldSalary: parseFloat(formData.oldSalary) || 0,
            newSalary: parseFloat(formData.newSalary) || 0,
            salaryType: formData.salaryType,
            documentDate: formData.documentDate,
            effectiveDate: formData.effectiveDate,
            amendmentReason: formData.amendmentReason,
            additionalChanges: formData.additionalChanges,
            companyName: companySettings?.name || "",
            companyAddress: companySettings
              ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}`
              : "",
          },
          false
        );

        const category = getDocumentCategory("contract_amendment");
        const typeName = getDocumentTypeName("contract_amendment");
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.amendmentReason || formData.effectiveDate}`,
          category,
          pdfDoc,
          user.id
        );

        if (result.success) {
          toast.success("Vertragsänderung gespeichert und in Dokumente abgelegt");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
          toast.error("Vertragsänderung gespeichert, PDF-Ablage fehlgeschlagen");
        }
      } catch (e) {
        console.error("Error archiving PDF:", e);
        toast.error("Vertragsänderung gespeichert, PDF-Ablage fehlgeschlagen");
      }

      setShowSignature(false);
      resetForm();
    } catch (error) {
      console.error("Error saving amendment:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const buildPdfPayload = () => ({
    personName: formData.personName,
    personAddress: formData.personAddress,
    oldPosition: formData.oldPosition,
    newPosition: formData.newPosition,
    oldSalary: parseFloat(formData.oldSalary) || 0,
    newSalary: parseFloat(formData.newSalary) || 0,
    salaryType: formData.salaryType,
    documentDate: formData.documentDate,
    effectiveDate: formData.effectiveDate,
    amendmentReason: formData.amendmentReason,
    additionalChanges: formData.additionalChanges,
    companyName: companySettings?.name || "",
    companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
  });

  const handlePreview = async () => {
    if (!formData.selectedEmployeeId) {
      toast.error("Bitte wählen Sie einen Mitarbeiter aus");
      return;
    }
    try {
      const pdfDoc = await exportContractAmendmentToPDF(buildPdfPayload(), false);
      setPreviewDoc(pdfDoc);
      setPreviewOpen(true);
    } catch (e) {
      console.error("Error generating preview:", e);
      toast.error("Fehler beim Erstellen der Vorschau");
    }
  };

  const handleExportPDF = async () => {
    if (!formData.selectedEmployeeId || !formData.effectiveDate) {
      toast.error("Bitte wählen Sie einen Mitarbeiter und ein Gültigkeitsdatum");
      return;
    }

    try {
      // Pass saveToFile: false to get the PDF document without auto-downloading
      const pdfDoc = await exportContractAmendmentToPDF({
        personName: formData.personName,
        personAddress: formData.personAddress,
        oldPosition: formData.oldPosition,
        newPosition: formData.newPosition,
        oldSalary: parseFloat(formData.oldSalary) || 0,
        newSalary: parseFloat(formData.newSalary) || 0,
        salaryType: formData.salaryType,
        documentDate: formData.documentDate,
        effectiveDate: formData.effectiveDate,
        amendmentReason: formData.amendmentReason,
        additionalChanges: formData.additionalChanges,
        companyName: companySettings?.name || "",
        companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
      }, false);

      // Save to employee documents
      if (user) {
        const category = getDocumentCategory('contract_amendment');
        const typeName = getDocumentTypeName('contract_amendment');
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.amendmentReason || formData.effectiveDate}`,
          category,
          pdfDoc,
          user.id
        );
        if (result.success) {
          toast.success("PDF wurde erstellt und in den Mitarbeiter-Dokumenten gespeichert");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
        }
      }

      // Always download the PDF for the user
      pdfDoc.save(`Vertragsaenderung_${formData.personName.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF wurde erstellt");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      selectedEmployeeId: "",
      personName: "",
      personAddress: "",
      oldPosition: "",
      newPosition: "",
      oldSalary: "",
      newSalary: "",
      salaryType: "monthly",
      documentDate: new Date().toISOString().split("T")[0],
      effectiveDate: "",
      amendmentReason: "",
      additionalChanges: "",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FilePen className="h-5 w-5" />
          Vertragsänderung erstellen
        </CardTitle>
        <CardDescription>
          Erstellen Sie eine Vertragsänderung für Gehaltsanpassungen oder Positionswechsel
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Employee Selection */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Mitarbeiter auswählen</h4>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Mitarbeiter *</Label>
              <Select value={formData.selectedEmployeeId} onValueChange={handleEmployeeSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dokumentendatum</Label>
              <Input 
                type="date"
                value={formData.documentDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, documentDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Gültig ab *</Label>
              <Input 
                type="date"
                value={formData.effectiveDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Position Changes */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Positionsänderung</h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Bisherige Position</Label>
              <Input value={formData.oldPosition} readOnly className="bg-muted" />
            </div>
            <div>
              <Label>Neue Position</Label>
              <Input 
                value={formData.newPosition} 
                onChange={(e) => setFormData(prev => ({ ...prev, newPosition: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Salary Changes */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Gehaltsänderung</h4>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Bisheriges Gehalt (EUR)</Label>
              <Input value={formData.oldSalary} readOnly className="bg-muted" />
            </div>
            <div>
              <Label>Neues Gehalt (EUR)</Label>
              <Input 
                type="number"
                value={formData.newSalary} 
                onChange={(e) => setFormData(prev => ({ ...prev, newSalary: e.target.value }))}
              />
            </div>
            <div>
              <Label>Gehaltsart</Label>
              <Select value={formData.salaryType} onValueChange={(value) => setFormData(prev => ({ ...prev, salaryType: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monatlich</SelectItem>
                  <SelectItem value="hourly">Stündlich</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Reason */}
        <div className="space-y-4">
          <div>
            <Label>Grund der Änderung</Label>
            <Input 
              value={formData.amendmentReason} 
              onChange={(e) => setFormData(prev => ({ ...prev, amendmentReason: e.target.value }))}
              placeholder="z.B. Leistungsbedingte Gehaltserhöhung, Beförderung..."
            />
          </div>
          <div>
            <Label>Weitere Änderungen</Label>
            <Textarea 
              value={formData.additionalChanges} 
              onChange={(e) => setFormData(prev => ({ ...prev, additionalChanges: e.target.value }))}
              placeholder="Weitere Vertragsänderungen..."
              rows={4}
            />
          </div>
        </div>

        {/* Signature Section */}
        {showSignature && (
          <div className="space-y-4 p-4 border rounded-lg">
            <h4 className="font-medium">Unterschrift des Arbeitnehmers</h4>
            <SignatureCanvas
              onSave={async (signatureDataUrl) => {
                const blob = await fetch(signatureDataUrl).then(r => r.blob());
                const fileName = `amendment-signature-${Date.now()}.png`;
                const { data: uploadData, error: uploadError } = await supabase.storage
                  .from("employee-profiles")
                  .upload(`signatures/${fileName}`, blob);

                if (uploadError) throw uploadError;

                const { data: urlData } = supabase.storage
                  .from("employee-profiles")
                  .getPublicUrl(`signatures/${fileName}`);

                await handleSave(urlData.publicUrl);
                setShowSignature(false);
              }}
              title="Unterschrift Mitarbeiter"
              description="Bitte unterschreiben Sie zur Bestätigung der Vertragsänderung"
              confirmationText="Mit Ihrer Unterschrift akzeptieren Sie die Änderungen."
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={handlePreview} disabled={saving} className="gap-2">
            <Eye className="h-4 w-4" />
            Vorschau
          </Button>
          <Button onClick={() => handleSave()} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Als Entwurf speichern
          </Button>
          <Button variant="outline" onClick={() => setShowSignature(true)} className="gap-2">
            <FilePen className="h-4 w-4" />
            Mit Unterschrift speichern
          </Button>
          <Button variant="outline" onClick={handleExportPDF} className="gap-2">
            <FileDown className="h-4 w-4" />
            PDF exportieren
          </Button>
        </div>
      </CardContent>
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        pdfDoc={previewDoc}
        title="Vorschau: Vertragsänderung"
        fileName={`Vertragsaenderung_${formData.personName.replace(/\s+/g, "_") || "Vorschau"}.pdf`}
      />
    </Card>
  );
};
